import { useMemo, useState, useCallback } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Layers,
  ListTree,
  Maximize2,
  Minimize2,
} from 'lucide-react';
import type { DashboardRow } from '@/shared/types';
import { Card } from '@/shared/ui/card/Card';
import { Button } from '@/shared/ui/button/Button';
import { Chip } from '@/shared/ui/chip/Chip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table/Table';
import { cn } from '@/shared/lib/utils';
import { pnlSections } from './pnlConfig';
import {
  buildPnLTree,
  calcVariation,
  extractPeriodColumns,
  formatPnLValue,
  getComparisonPeriods,
  getNumeric,
  type PeriodColumn,
  type PnLTreeNode,
} from './pnlUtils';

interface PnLTreeProps {
  rows: DashboardRow[];
  selectedMonths?: string[];
  gastosView?: 'detalle' | 'clasificacion';
  onGastosViewChange?: (view: 'detalle' | 'clasificacion') => void;
  /** Periodos (yearMonth, ej. "2025-04") abiertos que deben marcarse como preliminares. */
  preliminarMonths?: string[];
}

export function PnLTree({
  rows,
  selectedMonths,
  gastosView: gastosViewProp,
  onGastosViewChange: onGastosViewChangeProp,
  preliminarMonths,
}: PnLTreeProps) {
  const { periodColumns, tree, latestReal, prevMonth, prevYear } = useMemo(() => {
    const cols = extractPeriodColumns(rows);
    const filteredCols = selectedMonths?.length
      ? cols.filter(
          c =>
            selectedMonths.includes(c.yearMonth) ||
            selectedMonths.includes(c.key.replace(/ PRESUPUESTO/i, '').trim())
        )
      : cols;
    const comparison = getComparisonPeriods(filteredCols);

    return {
      periodColumns: filteredCols,
      tree: buildPnLTree(rows),
      ...comparison,
    };
  }, [rows, selectedMonths]);

  // Inicialmente expandir las secciones configuradas como defaultExpanded.
  const initialExpanded = useMemo(() => {
    const defaultExpanded = new Set(
      pnlSections.filter(s => s.defaultExpanded).map(s => s.id)
    );
    return new Set(tree.filter(n => defaultExpanded.has(n.id)).map(n => n.id));
  }, [tree]);

  const [expandedSections, setExpandedSections] =
    useState<Set<string>>(initialExpanded);

  const [internalGastosView, setInternalGastosView] = useState<'detalle' | 'clasificacion'>('detalle');

  const gastosView = gastosViewProp ?? internalGastosView;
  const setGastosView = useCallback(
    (view: 'detalle' | 'clasificacion') => {
      onGastosViewChangeProp?.(view);
      setInternalGastosView(view);
    },
    [onGastosViewChangeProp]
  );

  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedSections(new Set(tree.map(n => n.id)));
  }, [tree]);

  const collapseAll = useCallback(() => {
    setExpandedSections(new Set());
  }, []);

  const realColumns = periodColumns.filter(c => !c.isBudget);

  return (
    <Card padding="none" className="overflow-hidden">
      {/* Toolbar */}
      <div className="flex flex-col gap-4 border-b border-border bg-surface-elevated/50 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="font-display text-lg font-semibold text-text-primary">
            Estado de Resultados
          </h3>
          <p className="text-sm text-text-secondary">
            Comparativo por periodo con variaciones.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={expandAll}
            className="h-9 gap-1.5"
          >
            <Maximize2 className="h-3.5 w-3.5" />
            Expandir
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={collapseAll}
            className="h-9 gap-1.5"
          >
            <Minimize2 className="h-3.5 w-3.5" />
            Colapsar
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="h-[calc(100vh-300px)] overflow-auto">
        <Table wrapperClassName="overflow-visible">
          <TableHeader className="sticky top-0 z-20 bg-surface-elevated/90 backdrop-blur-xl">
            <TableRow className="hover:bg-transparent">
              <TableHead className="sticky left-0 z-30 min-w-[300px] bg-surface-elevated/90 backdrop-blur-xl">
                Concepto
              </TableHead>
              {realColumns.map(col => (
                <TableHead
                  key={col.key}
                  className={cn(
                    'min-w-[130px] whitespace-nowrap text-right',
                    latestReal?.key === col.key && 'text-primary'
                  )}
                >
                  <div className="flex items-center justify-end gap-1.5">
                    {col.yearMonth}
                    {preliminarMonths?.includes(col.yearMonth) && (
                      <Chip variant="warning" size="sm">
                        Preliminar
                      </Chip>
                    )}
                  </div>
                </TableHead>
              ))}
              {prevMonth && (
                <TableHead className="min-w-[130px] whitespace-nowrap text-right text-text-muted">
                  Δ vs Mes Ant.
                </TableHead>
              )}
              {prevMonth && (
                <TableHead className="min-w-[110px] whitespace-nowrap text-right text-text-muted">
                  % vs Mes Ant.
                </TableHead>
              )}
              {prevYear && (
                <TableHead className="min-w-[110px] whitespace-nowrap text-right text-text-muted">
                  % vs Año Ant.
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {tree.map(section => {
              const isExpanded = expandedSections.has(section.id);
              return (
                <SectionRows
                  key={section.id}
                  section={section}
                  isExpanded={isExpanded}
                  onToggle={() => toggleSection(section.id)}
                  periodColumns={periodColumns}
                  realColumns={realColumns}
                  latestReal={latestReal}
                  prevMonth={prevMonth}
                  prevYear={prevYear}
                  gastosView={gastosView}
                  onGastosViewChange={setGastosView}
                />
              );
            })}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

interface SectionRowsProps {
  section: PnLTreeNode;
  isExpanded: boolean;
  onToggle: () => void;
  periodColumns: PeriodColumn[];
  realColumns: PeriodColumn[];
  latestReal?: PeriodColumn;
  prevMonth?: PeriodColumn;
  prevYear?: PeriodColumn;
  gastosView: 'detalle' | 'clasificacion';
  onGastosViewChange: (view: 'detalle' | 'clasificacion') => void;
}

function SectionRows({
  section,
  isExpanded,
  onToggle,
  periodColumns,
  realColumns,
  latestReal,
  prevMonth,
  prevYear,
  gastosView,
  onGastosViewChange,
}: SectionRowsProps) {
  const Icon = isExpanded ? ChevronDown : ChevronRight;
  const isGastosSection = section.id === 'gastos-operacion';

  const visibleChildren = useMemo(() => {
    if (!isGastosSection) return section.children ?? [];
    return (section.children ?? []).filter(
      child => child.groupMode === 'all' || child.groupMode === gastosView
    );
  }, [isGastosSection, section.children, gastosView]);

  return (
    <>
      {/* Section header */}
      <TableRow
        onClick={onToggle}
        className="cursor-pointer bg-primary-muted/40 hover:bg-primary-muted/60"
      >
        <TableCell
          className={cn(
            'sticky left-0 z-10 bg-primary-muted/40 py-3 font-display font-semibold text-text-primary'
          )}
        >
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-primary" />
            <span>{section.label}</span>
            <span className="ml-2 text-xs font-normal text-text-muted">
              {visibleChildren.length} conceptos
            </span>
            {isGastosSection && (
              <div
                className="ml-4 flex items-center rounded-lg border border-border bg-surface p-0.5"
                onClick={e => e.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={() => onGastosViewChange('detalle')}
                  className={cn(
                    'flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors',
                    gastosView === 'detalle'
                      ? 'bg-primary text-white'
                      : 'text-text-secondary hover:bg-surface-elevated'
                  )}
                >
                  <ListTree className="h-3 w-3" />
                  Detalle
                </button>
                <button
                  type="button"
                  onClick={() => onGastosViewChange('clasificacion')}
                  className={cn(
                    'flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors',
                    gastosView === 'clasificacion'
                      ? 'bg-primary text-white'
                      : 'text-text-secondary hover:bg-surface-elevated'
                  )}
                >
                  <Layers className="h-3 w-3" />
                  Clasificación
                </button>
              </div>
            )}
          </div>
        </TableCell>
        <EmptyCells count={realColumns.length} />
        {prevMonth && <EmptyCells count={2} />}
        {prevYear && <EmptyCells count={1} />}
      </TableRow>

      {/* Child rows */}
      {isExpanded &&
        visibleChildren.map(child => (
          <DataRow
            key={child.id}
            node={child}
            periodColumns={periodColumns}
            realColumns={realColumns}
            latestReal={latestReal}
            prevMonth={prevMonth}
            prevYear={prevYear}
          />
        ))}
    </>
  );
}

interface DataRowProps {
  node: PnLTreeNode;
  periodColumns: PeriodColumn[];
  realColumns: PeriodColumn[];
  latestReal?: PeriodColumn;
  prevMonth?: PeriodColumn;
  prevYear?: PeriodColumn;
}

function DataRow({
  node,
  realColumns,
  latestReal,
  prevMonth,
  prevYear,
}: DataRowProps) {
  const row = node.row;

  const latestValue = latestReal && row ? getNumeric(row, latestReal.key) : null;
  const prevMonthValue =
    prevMonth && row ? getNumeric(row, prevMonth.key) : null;
  const prevYearValue =
    prevYear && row ? getNumeric(row, prevYear.key) : null;

  const monthChangeAmount =
    latestValue !== null && prevMonthValue !== null
      ? latestValue - prevMonthValue
      : null;
  const monthChange =
    latestValue !== null && prevMonthValue !== null
      ? calcVariation(latestValue, prevMonthValue)
      : null;
  const yearChange =
    latestValue !== null && prevYearValue !== null
      ? calcVariation(latestValue, prevYearValue)
      : null;

  return (
    <TableRow
      className={cn(
        node.isTotal && 'bg-secondary-muted/20 font-semibold',
        node.level === 0 && 'bg-surface-elevated/40'
      )}
    >
      <TableCell
        className={cn(
          'sticky left-0 z-10 whitespace-nowrap bg-surface',
          node.isTotal
            ? 'font-display font-semibold text-text-primary'
            : 'text-text-secondary',
          node.level === 2 && 'pl-10'
        )}
      >
        {node.label}
      </TableCell>

      {realColumns.map(col => {
        const value = row ? getNumeric(row, col.key) : null;
        return (
          <TableCell
            key={col.key}
            className={cn(
              'whitespace-nowrap text-right tabular-nums',
              latestReal?.key === col.key
                ? 'font-medium text-text-primary'
                : 'text-text-secondary'
            )}
          >
            {formatPnLValue(value, node.format)}
          </TableCell>
        );
      })}

      {prevMonth && (
        <TableCell
          className={cn(
            'whitespace-nowrap text-right tabular-nums',
            monthChangeAmount !== null &&
              monthChangeAmount < 0 &&
              'text-danger-text',
            monthChangeAmount !== null &&
              monthChangeAmount > 0 &&
              'text-success-text'
          )}
        >
          {formatPnLValue(monthChangeAmount, node.format)}
        </TableCell>
      )}

      {prevMonth && (
        <TableCell className="whitespace-nowrap text-right tabular-nums">
          <VariationBadge value={monthChange} />
        </TableCell>
      )}

      {prevYear && (
        <TableCell className="whitespace-nowrap text-right tabular-nums">
          <VariationBadge value={yearChange} />
        </TableCell>
      )}
    </TableRow>
  );
}

function EmptyCells({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <TableCell key={i} className="bg-transparent" />
      ))}
    </>
  );
}

function VariationBadge({ value }: { value: number | null }) {
  if (value === null || !Number.isFinite(value)) {
    return <span className="text-text-muted">—</span>;
  }

  const isPositive = value >= 0;
  return (
    <span
      className={cn(
        'inline-flex items-center justify-end gap-1 text-xs font-semibold',
        isPositive ? 'text-success-text' : 'text-danger-text'
      )}
    >
      {isPositive ? '+' : ''}
      {value.toFixed(1)}%
    </span>
  );
}
